import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import type { SenderRule } from "../entities/counterparties";
import {
  matchTypeLabels,
  resolutionLadder,
  ruleImportance,
  importanceLabels,
  importanceValues,
  type RuleImportance,
  validateDomain,
  normalizeDomain,
  validateEmail,
} from "../entities/counterparties";
import { TextField } from "./CounterpartyFields";
import {
  formatDate,
  requestErrorText,
  serverFieldErrors,
  useSubmissionKey,
} from "./counterpartiesSupport";
import type { ShellServices } from "./services";
import { counterpartyKeys, useCounterpartyNames } from "./useCounterpartyNames";
import { Select } from "./Select";
import { Notice } from "./Notice";

type CounterpartyService = NonNullable<ShellServices["counterparties"]>;

const RULES_LIMIT = 100;

/** Что учитывается при определении отправителя и что важнее. */
function ResolutionLadder() {
  return (
    <details className="cp-ladder" open>
      <summary>Как система определяет контрагента по отправителю</summary>
      <p>
        Основания проверяются сверху вниз: чем выше в списке, тем оно важнее.
        Если самое важное основание подошло сразу нескольким контрагентам,
        система ничего не выбирает сама — письмо получает статус «Конфликт» и
        ждёт вашего решения. Название организации, ИНН и КПП из письма не
        учитываются: их может написать кто угодно.
      </p>
      <ol className="cp-ladder__list">
        {resolutionLadder.map((step) => (
          <li key={step.key}>
            <strong>{step.label}</strong>
            <span className="muted">{step.hint}</span>
          </li>
        ))}
      </ol>
    </details>
  );
}

const importanceBadge: Record<RuleImportance, string> = {
  high: "status-badge--active",
  normal: "status-badge--expired",
  low: "status-badge--pending",
};

function RuleImportanceBadge({ rule }: { rule: SenderRule }) {
  const level = ruleImportance(rule.priority);
  return (
    <span className={`status-badge ${importanceBadge[level]}`}>
      {importanceLabels[level]}
    </span>
  );
}

function RuleForm({
  service,
  counterpartyId,
  options,
  onCreated,
}: {
  service: CounterpartyService;
  /** Фиксированный контрагент (карточка) либо выбор из списка (общая вкладка). */
  counterpartyId?: string;
  options: readonly { id: string; name: string }[];
  onCreated: () => void;
}) {
  const key = useSubmissionKey();
  const [target, setTarget] = useState(counterpartyId ?? "");
  const [matchType, setMatchType] = useState<"email" | "domain">("email");
  const [pattern, setPattern] = useState("");
  const [importance, setImportance] = useState<RuleImportance>("normal");
  const [submitted, setSubmitted] = useState(false);

  const create = useMutation({
    mutationFn: () =>
      service.createRule(
        {
          counterparty_id: counterpartyId ?? target,
          match_type: matchType,
          pattern:
            matchType === "domain"
              ? normalizeDomain(pattern)
              : pattern.trim().toLowerCase(),
          priority: importanceValues[importance],
        },
        key.current(),
      ),
    onSuccess: () => {
      key.reset();
      setPattern("");
      setSubmitted(false);
      onCreated();
    },
  });

  const errors = {
    counterparty:
      counterpartyId === undefined && target === ""
        ? "Выберите контрагента"
        : null,
    pattern:
      matchType === "email" ? validateEmail(pattern) : validateDomain(pattern),
  };
  const server = serverFieldErrors(create.error);

  function submit(event: FormEvent) {
    event.preventDefault();
    setSubmitted(true);
    if (Object.values(errors).some((value) => value !== null)) return;
    create.mutate();
  }

  return (
    <form className="cp-rule-form" onSubmit={submit} noValidate>
      <h3>Новое правило</h3>
      {create.isError ? (
        <Notice>
          {requestErrorText(create.error, "Не удалось создать правило.")}
        </Notice>
      ) : null}
      {counterpartyId === undefined ? (
        <div className="field cp-field">
          <label htmlFor="cp-rule-counterparty">
            <span>Контрагент</span>
          </label>
          <Select
            id="cp-rule-counterparty"
            value={target}
            aria-invalid={submitted && errors.counterparty ? true : undefined}
            onChange={(event) => {
              key.reset();
              setTarget(event.target.value);
            }}
          >
            <option value="">Выберите контрагента</option>
            {options.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </Select>
          {submitted && errors.counterparty ? (
            <small className="field-error" role="alert">
              {errors.counterparty}
            </small>
          ) : null}
        </div>
      ) : null}
      <div className="field cp-field">
        <label htmlFor="cp-rule-type">
          <span>Совпадение по</span>
        </label>
        <Select
          id="cp-rule-type"
          value={matchType}
          onChange={(event) => {
            key.reset();
            setMatchType(event.target.value as "email" | "domain");
          }}
        >
          <option value="email">Адресу отправителя</option>
          <option value="domain">Домену отправителя</option>
        </Select>
      </div>
      <TextField
        label={
          matchType === "email" ? "Email отправителя" : "Домен отправителя"
        }
        value={pattern}
        placeholder={matchType === "email" ? "buh@example.ru" : "example.ru"}
        autoComplete="off"
        error={
          submitted
            ? (errors.pattern ?? server.pattern ?? null)
            : (server.pattern ?? null)
        }
        onChange={(value) => {
          key.reset();
          setPattern(value);
        }}
      />
      <div className="field cp-field">
        <label htmlFor="cp-rule-importance">
          <span>Важность правила</span>
        </label>
        <Select
          id="cp-rule-importance"
          value={importance}
          onChange={(event) => {
            key.reset();
            setImportance(event.target.value as RuleImportance);
          }}
        >
          {(Object.keys(importanceLabels) as RuleImportance[]).map((level) => (
            <option key={level} value={level}>
              {importanceLabels[level]}
            </option>
          ))}
        </Select>
        <small className="cp-hint">
          Нужна, только если к одному отправителю подходят два правила одного
          вида: побеждает более важное. Если важность одинаковая, письмо
          попадёт в «Конфликт».
        </small>
      </div>
      <button
        className="button button--primary button--compact"
        disabled={create.isPending}
      >
        {create.isPending ? "Создаём…" : "Создать правило"}
      </button>
    </form>
  );
}

/**
 * Правила определения отправителя. В Core их можно только создать и удалить:
 * правки правила там нет, поэтому изменить правило можно, лишь удалив его и
 * создав заново.
 */
export function SenderRulesPanel({
  service,
  canManage,
  counterpartyId,
}: {
  service: CounterpartyService;
  canManage: boolean;
  /** В карточке контрагента список сужается до его правил. */
  counterpartyId?: string;
}) {
  const client = useQueryClient();
  const [confirming, setConfirming] = useState<string | null>(null);
  const rules = useQuery({
    queryKey: counterpartyKeys.rules(counterpartyId ?? null),
    queryFn: () =>
      service.rules({
        per_page: RULES_LIMIT,
        ...(counterpartyId === undefined
          ? {}
          : { counterparty_id: counterpartyId }),
      }),
  });
  const items = rules.data?.data ?? [];
  const { names, options } = useCounterpartyNames(
    service,
    items.map((rule) => rule.counterparty_id),
    // В карточке контрагента названия не нужны: правила уже относятся к нему.
    { enabled: counterpartyId === undefined },
  );
  const remove = useMutation({
    mutationFn: (ruleId: string) => service.deleteRule(ruleId),
    onSuccess: async () => {
      setConfirming(null);
      await client.invalidateQueries({
        queryKey: ["document-workflow", "counterparties", "rules"],
      });
    },
  });
  const reload = () =>
    client.invalidateQueries({
      queryKey: ["document-workflow", "counterparties", "rules"],
    });

  // В карточке контрагента заголовок вложен в неё, а не открывает страницу.
  const Heading = counterpartyId === undefined ? "h2" : "h3";

  return (
    <section className="cp-rules" aria-labelledby="cp-rules-title">
      <Heading id="cp-rules-title">
        {counterpartyId === undefined
          ? "Правила определения отправителя"
          : "Правила для этого контрагента"}
      </Heading>
      {counterpartyId === undefined ? <ResolutionLadder /> : null}

      {rules.isLoading ? <p aria-busy="true">Загружаем правила…</p> : null}
      {rules.isError ? (
        <Notice>
          {requestErrorText(rules.error, "Не удалось загрузить правила.")}{" "}
          <button
            type="button"
            className="button button--text button--compact"
            onClick={() => void rules.refetch()}
          >
            Повторить
          </button>
        </Notice>
      ) : null}
      {remove.isError ? (
        <Notice>
          {requestErrorText(remove.error, "Не удалось удалить правило.")}
        </Notice>
      ) : null}

      {rules.isSuccess && items.length === 0 ? (
        <p className="cp-empty">
          Правил пока нет. Отправитель определяется по email контактов и доменам
          контрагентов; правило появится, когда вы вручную сопоставите письмо и
          отметите «запомнить отправителя», либо когда создадите его здесь.
        </p>
      ) : null}

      {items.length > 0 ? (
        <div className="cp-table-wrap">
          <table className="cp-table">
            <thead>
              <tr>
                <th scope="col">Основание</th>
                <th scope="col">Значение</th>
                {counterpartyId === undefined ? (
                  <th scope="col">Контрагент</th>
                ) : null}
                <th scope="col">Важность</th>
                <th scope="col">Создано</th>
                {canManage ? (
                  <th scope="col">
                    <span className="visually-hidden">Действия</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {items.map((rule) => (
                <tr key={rule.id}>
                  <td data-label="Основание">
                    {matchTypeLabels[rule.match_type]}
                  </td>
                  <td data-label="Значение">
                    <strong>{rule.pattern}</strong>
                  </td>
                  {counterpartyId === undefined ? (
                    <td data-label="Контрагент">
                      {names.get(rule.counterparty_id) ?? "Загружаем…"}
                    </td>
                  ) : null}
                  <td data-label="Важность">
                    <RuleImportanceBadge rule={rule} />
                  </td>
                  <td data-label="Создано">{formatDate(rule.created_at)}</td>
                  {canManage ? (
                    <td data-label="Действия">
                      {confirming === rule.id ? (
                        <span
                          className="cp-confirm"
                          role="group"
                          aria-label="Подтверждение удаления"
                        >
                          <button
                            type="button"
                            className="button button--danger button--compact"
                            disabled={remove.isPending}
                            onClick={() => remove.mutate(rule.id)}
                          >
                            Удалить правило
                          </button>
                          <button
                            type="button"
                            className="button button--text button--compact"
                            onClick={() => setConfirming(null)}
                          >
                            Отмена
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="button button--text button--compact"
                          aria-label={`Удалить правило ${rule.pattern}`}
                          onClick={() => setConfirming(rule.id)}
                        >
                          Удалить
                        </button>
                      )}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {(rules.data?.meta.total ?? 0) > RULES_LIMIT ? (
        <p className="muted">
          Показаны первые {RULES_LIMIT} из {rules.data?.meta.total} правил.
        </p>
      ) : null}

      {canManage ? (
        <RuleForm
          key={counterpartyId ?? "all"}
          service={service}
          counterpartyId={counterpartyId}
          options={options}
          onCreated={() => void reload()}
        />
      ) : (
        <p className="muted">
          Создавать и удалять правила может сотрудник с правом «Управление
          контрагентами».
        </p>
      )}
    </section>
  );
}
